<script setup lang="ts">
  import { computed } from 'vue';
  import { Checkbox, IconButton, InfoTooltip, Tooltip } from '@/shared/ui';
  import { formatCivilDateToPtBR, formatInstantToPtBR } from '@/shared/lib';
  import { type TrainingListItem } from '@/entities/Training';
  import TrainingStatusBadge from './TrainingStatusBadge.vue';
  import TrainingListCollaborators from './TrainingListCollaborators.vue';
  import { TRAINING_EMPTY_VALUE } from '../lib/trainingEmptyValue';
  import { formatRevisionNumber } from '../lib/formatRevisionNumber';
  import { isTrainingControlType } from '../lib/isTrainingControlType';
  import { TRAINING_LIST_TYPE_LABELS } from '../lib/trainingListTypeLabels';
  import EyeActionIcon from '@/shared/assets/icons/EyeActionIcon.svg';
  import PencilBlackIcon from '@/shared/assets/icons/PencilBlack.svg';
  import TrashCurrentColorIcon from '@/shared/assets/icons/TrashCurrentColor.svg';
  import Lesson1Icon from '@/shared/assets/icons/Lesson1Icon.svg';
  import InfoIcon from '@/shared/assets/icons/InfoIcon.svg';

  const props = defineProps<{
    training: TrainingListItem;
    selected: boolean;
  }>();

  const emit = defineEmits<{
    toggle: [training: TrainingListItem];
    view: [training: TrainingListItem];
    edit: [training: TrainingListItem];
    delete: [training: TrainingListItem];
    control: [training: TrainingListItem];
  }>();

  const revisionLabel = computed(() =>
    props.training.revision !== null
      ? formatRevisionNumber(props.training.revision)
      : TRAINING_EMPTY_VALUE
  );

  const scheduledDateLabel = computed(() =>
    props.training.scheduledDate
      ? formatCivilDateToPtBR(props.training.scheduledDate)
      : TRAINING_EMPTY_VALUE
  );

  const trainingAudit = computed(() => {
    const { createdAt, createdBy, updatedAt, updatedBy } = props.training.audit;
    const wasEdited = updatedBy !== null;

    return {
      createdAt: formatInstantToPtBR(createdAt),
      createdBy: createdBy?.name,
      updatedAt: formatInstantToPtBR(wasEdited ? updatedAt : createdAt),
      updatedBy: (wasEdited ? updatedBy : createdBy)?.name,
    };
  });
</script>

<template>
  <tr class="border-b border-gray-02 hover:bg-gray-01 transition-colors">
    <td class="px-4 py-3 w-12 align-middle text-center">
      <Checkbox
        :id="training.id"
        :checked="selected"
        @toggle="emit('toggle', training)"
      />
    </td>
    <td class="px-4 py-3 text-sm text-gray-07">
      {{ training.title }}
    </td>
    <td class="px-4 py-3 text-sm text-gray-07">
      {{ revisionLabel }}
    </td>
    <td class="px-4 py-3 text-sm text-gray-07 whitespace-nowrap">
      {{ scheduledDateLabel }}
    </td>
    <td class="px-4 py-3 text-sm text-gray-07 whitespace-nowrap">
      <TrainingListCollaborators :collaborators="training.collaborators" />
    </td>
    <td class="px-4 py-3 text-sm text-gray-07">
      {{ TRAINING_LIST_TYPE_LABELS[training.type] }}
    </td>
    <td class="px-4 py-3">
      <TrainingStatusBadge :status="training.status" />
    </td>
    <td class="px-4 py-3">
      <div class="flex gap-2 items-center text-gray-xx-02">
        <Tooltip content="Visualizar" class="flex items-center">
          <IconButton
            :src="EyeActionIcon"
            alt="Visualizar"
            class="w-6 h-6"
            :on-click="() => emit('view', training)"
          />
        </Tooltip>
        <Tooltip content="Editar" class="flex items-center">
          <IconButton
            :src="PencilBlackIcon"
            alt="Editar"
            class="w-6 h-6"
            :on-click="() => emit('edit', training)"
          />
        </Tooltip>
        <Tooltip content="Deletar" class="flex items-center">
          <IconButton
            :src="TrashCurrentColorIcon"
            alt="Deletar"
            class="w-6 h-6"
            :on-click="() => emit('delete', training)"
          />
        </Tooltip>
        <Tooltip
          v-if="isTrainingControlType(training.type)"
          content="Controle de Treinamento"
          class="flex items-center"
        >
          <IconButton
            :src="Lesson1Icon"
            alt="Controle de Treinamento"
            class="w-6 h-6"
            :on-click="() => emit('control', training)"
          />
        </Tooltip>
        <InfoTooltip :icon="InfoIcon" v-bind="trainingAudit">
          <IconButton :src="InfoIcon" alt="Informações" class="w-6 h-6" />
        </InfoTooltip>
      </div>
    </td>
  </tr>
</template>
